// =============================================
// Pillar Generator (STOP-P2 faza P)
//
// Osobny entry point dla pillar pages — analog `authorPageGenerator.ts`.
// Klon spokowego mechanizmu linkowania (PRE-STEP relations + LLM inline links + post-check).
//
// Pipeline (12 stages):
// 1. Load context — getPillarClusterContext
// 2. PRE-STEP — createHubToSpokeRelations (thematic_node_relations)
// 3. Outline — outline_generation_pillar (LLM, JSON h2_sections)
// 4. Intro — intro_generation_pillar (LLM, <p>)
// 5. Sections per H2 — section_generation_pillar (LLM, <h2>+<p>+<a href>)
// 6. Concatenate intro + sections
// 7. Optimization — optimizeArticle (share)
// 8. Humanization — humanizeArticle (share)
// 9. (skipped in P2) insertSources — wymaga claim extraction, postponed do P5
// 10. postCheckPillarLinks — dedupe + footer + refresh relations (NEW STOP-P1)
// 11. (skipped in P2) postContentAudit — wymaga NodeContentContext (spoke schema), P5 osobny QA dla pillar
// 12. Persist — UPDATE thematic_node_content
//
// PARITY: zero zmian w spoke flow (osobny plik, generateArticle nietknięty).
// Gate runtime: VITE_PILLAR_FLOW_V2_ENABLED (UI wybiera czy wywołać generatePillar vs stary isPillar branch).
// =============================================

import { supabase } from "@/integrations/supabase/client";
import { loadPrompt } from "@/services/llm/promptLoader";
import { getLLMProvider, getStageConfig } from "@/services/llm";
import { compilePromptSafe } from "./promptCompiler";
import { optimizeArticle } from "./optimizationService";
import { humanizeArticle } from "./humanizationService";
import { postCheckPillarLinks, type PillarPostCheckSpoke, type PillarLinkPostCheckResult } from "./pillarLinkPostCheck";
import {
  getPillarClusterContext,
  formatPillarContextAsVars,
  createHubToSpokeRelations,
  extractSlugFromUrl,
  type PillarClusterContext,
  type PillarSpokeMember,
} from "./pillarClusterContext";
import {
  logPublic as _logPublicRaw,
  logDebug as _logDebugRaw,
  logError as _logErrorRaw,
  logVerbose as _logVerboseRaw,
  logLlm,
  startLogRun,
  endLogRun,
} from "./contentLogger";

// P-D (faza P): lokalne wrappery -> kategoria "content-engine" zeby pillar runy
// pojawialy sie w panelu LOGI (FAB "Logi" -> filtr content-engine).
// Bez tego logPublic/Error/Debug uzywaja default category="system" -> niewidoczne.
const logPublic = (prefix: string, msg: string) => _logPublicRaw(prefix, msg, "content-engine");
const logError = (prefix: string, msg: string, err?: unknown) => _logErrorRaw(prefix, msg, err, "content-engine");
const logDebug = (prefix: string, msg: string, data?: Record<string, unknown>) => _logDebugRaw(prefix, msg, data, "content-engine");
const logVerbose = (prefix: string, msg: string, data?: Record<string, unknown>) => _logVerboseRaw(prefix, msg, data, "content-engine");

// =============================================
// Public types
// =============================================

export interface PillarH2Section {
  title: string;
  h3_subpoints?: string[];
  internal_links?: string[];
}

export interface PillarOutline {
  h2_sections: PillarH2Section[];
}

export interface PillarGenerationDebug {
  outline_prompt_key: string;
  intro_prompt_key: string;
  section_prompt_key: string;
  outline_chars: number;
  intro_chars: number;
  sections_count: number;
  raw_html_chars: number;
  optimized_html_chars: number;
  final_html_chars: number;
  cluster_member_spokes_count: number;
  published_spokes_count: number;
  cluster_member_entities_count: number;
  pre_step_relations_created: number;
  pre_step_relations_deleted: number;
  post_check: PillarLinkPostCheckResult | null;
  // P-A (faza P): per-section link distribution (z outline.section.internal_links → spoki published)
  per_section_link_counts: Array<{ h2: string; assigned: number; total_published: number }>;
  // P-G (faza P hotfix): omission guard retry — LLM pominal assigned slug -> retry RAZ z mandatory inject
  per_section_retries: Array<{ h2: string; missing_before: string[]; missing_after: string[]; recovered: number; retry_cost_usd: number; outcome: "retry_recovered_all" | "retry_partial" | "retry_no_improvement" }>;
  total_cost_usd: number;
  total_duration_ms: number;
}

export interface PillarGenerationResult {
  success: boolean;
  html?: string;
  costUsd: number;
  pillarNodeId: string;
  mapId: string;
  error?: string;
  debug?: PillarGenerationDebug;
}

const PILLAR_FALLBACK_OUTLINE: PillarOutline = {
  h2_sections: [
    { title: "Przegląd silosa", h3_subpoints: [], internal_links: [] },
  ],
};

// =============================================
// Helpers
// =============================================

/**
 * Convert PillarSpokeMember[] -> PillarPostCheckSpoke[] (z slug_path wyciągniętym z wp_post_url).
 * Tylko published spoki z wp_post_url -> slug_path.
 */
function mapSpokesForPostCheck(spokes: PillarSpokeMember[]): PillarPostCheckSpoke[] {
  return spokes
    .map((s) => {
      const slugPath = extractSlugFromUrl(s.wp_post_url);
      return {
        node_id: s.node_id,
        title: s.title,
        slug_path: slugPath ?? "",
        is_published: s.is_published,
        wp_post_url: s.wp_post_url,
      };
    })
    .filter((s) => !!s.slug_path); // skip jesli slug_path nie udalo sie wyciagnac
}

/**
 * P-G (faza P hotfix): pure helper - znajdz assigned slugi ktore NIE wystepuja jako href="/slug" w HTML sekcji.
 *
 * LLM bywa pominac assigned link (omission, NIE typo - P-F nie pomoze).
 * Ta funkcja zwraca brakujace slugi -> Stage 5 wywoluje retry RAZ z mandatory inject block.
 *
 * Pure, deterministyczna, exportowana dla unit testow.
 */
export function findMissingAssignedLinks(html: string, assignedSlugs: string[]): string[] {
  if (assignedSlugs.length === 0) return [];
  const found = new Set<string>();
  const regex = /href="(\/[^"]+)"/gi;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(html)) !== null) {
    found.add(m[1]);
  }
  return assignedSlugs.filter((slug) => !found.has(slug));
}

/**
 * Parse outline JSON response. Fallback do PILLAR_FALLBACK_OUTLINE gdy parse fail.
 */
function parsePillarOutline(rawText: string): PillarOutline {
  try {
    // Strip markdown code fences if present
    const cleaned = rawText
      .replace(/^```json\s*/i, "")
      .replace(/^```\s*/i, "")
      .replace(/\s*```\s*$/i, "")
      .trim();
    const parsed = JSON.parse(cleaned);
    if (parsed && Array.isArray(parsed.h2_sections) && parsed.h2_sections.length > 0) {
      return parsed as PillarOutline;
    }
    logError("PillarGen", "Outline JSON has no h2_sections, using fallback");
    return PILLAR_FALLBACK_OUTLINE;
  } catch (err) {
    logError("PillarGen", `Outline JSON parse failed: ${(err as Error).message}`);
    return PILLAR_FALLBACK_OUTLINE;
  }
}

// =============================================
// Main entry point
// =============================================

/**
 * Generuje pillar page dla danego pillar node.
 * Idempotent: re-run nadpisuje thematic_node_content.
 * NON-BLOCKING fail: blad LLM/optymalizacji loguje i przechodzi do persist z best-effort HTML.
 */
export async function generatePillar(
  pillarNodeId: string,
  mapId: string,
  onProgress?: (msg: string, stage?: string) => void,
): Promise<PillarGenerationResult> {
  const runId = `pillar-${pillarNodeId.slice(0, 8)}-${Date.now()}`;
  startLogRun(runId, { type: "pillar_generation", pillarNodeId, mapId });
  logPublic("PillarGen", `START — pillar=${pillarNodeId.slice(0, 8)} map=${mapId.slice(0, 8)}`);
  onProgress?.("Loading context...", "load");
  const startTime = Date.now();

  let totalCostUsd = 0;
  let preStepCreated = 0;
  let preStepDeleted = 0;

  // ---------------------------------------------
  // Stage 1: Load context
  // ---------------------------------------------
  const ctx = await getPillarClusterContext(mapId, pillarNodeId);
  if (!ctx) {
    const msg = `Pillar context not loaded (pillar=${pillarNodeId}, map=${mapId})`;
    logError("PillarGen", msg);
    endLogRun({ success: false, error: msg });
    return { success: false, costUsd: 0, pillarNodeId, mapId, error: msg };
  }

  // Get pillar node user_id (potrzebne dla createHubToSpokeRelations + UPSERT)
  const { data: pillarNodeData } = await (supabase as any)
    .from("thematic_nodes")
    .select("user_id, title")
    .eq("id", pillarNodeId)
    .single();

  if (!pillarNodeData) {
    const msg = `Pillar node ${pillarNodeId} not found`;
    logError("PillarGen", msg);
    endLogRun({ success: false, error: msg });
    return { success: false, costUsd: 0, pillarNodeId, mapId, error: msg };
  }
  const pillarUserId = pillarNodeData.user_id as string;
  const pillarTitle = pillarNodeData.title as string;

  const publishedSpokes = ctx.cluster_member_spokes.filter((s) => s.is_published && s.wp_post_url);
  // P-D: metryki danych w logPublic (widoczne w panelu LOGI prod) — diagnoza "za malo danych".
  logPublic(
    "PillarGen",
    `CONTEXT seed="${ctx.seed_name || "?"}" | spokes: ${ctx.cluster_member_spokes.length} total (${publishedSpokes.length} published, ${ctx.cluster_member_spokes.length - publishedSpokes.length} planned) | entities: ${ctx.cluster_member_entities.length} | macro_chars: ${(ctx.macro_statement || "").length}`,
  );
  // Wczesny WARNING gdy danych malo — diagnostyka "chudego pillara"
  if (publishedSpokes.length === 0) {
    logError("PillarGen", `WARNING: 0 published spokes — pillar bedzie bez linkow w prozie (tylko relations + treść)`);
  }
  if (ctx.cluster_member_entities.length < 3) {
    logError("PillarGen", `WARNING: tylko ${ctx.cluster_member_entities.length} encji — treść moze byc chuda. Sprawdz primary_entity_id/required_entity_ids spokow seeda.`);
  }

  // ---------------------------------------------
  // Stage 2: PRE-STEP — createHubToSpokeRelations
  // ---------------------------------------------
  onProgress?.("Creating hub_to_spoke relations...", "pre_step_relations");
  try {
    const preStep = await createHubToSpokeRelations(pillarNodeId, mapId, pillarUserId, publishedSpokes);
    preStepCreated = preStep.created;
    preStepDeleted = preStep.deleted;
    logPublic("PillarGen", `PRE-STEP relations: created=${preStepCreated} deleted=${preStepDeleted}`);
  } catch (err) {
    logError("PillarGen", `PRE-STEP relations exception (non-blocking): ${(err as Error).message}`);
  }

  // ---------------------------------------------
  // Build template vars (Cowork v4 contract)
  // ---------------------------------------------
  const pillarVars = formatPillarContextAsVars(ctx);
  logDebug("PillarGen", "Template vars built", { keys: Object.keys(pillarVars) });
  logVerbose("PillarGen", "Template vars (FULL)", pillarVars);

  // ---------------------------------------------
  // Stage 3: Outline (outline_generation_pillar)
  // ---------------------------------------------
  onProgress?.("Generating outline...", "outline");
  const outlinePromptDb = await loadPrompt("outline_generation_pillar" as never);
  if (!outlinePromptDb) {
    const msg = "Prompt 'outline_generation_pillar' not found in DB (is_active=true required)";
    logError("PillarGen", msg);
    endLogRun({ success: false, error: msg });
    return { success: false, costUsd: totalCostUsd, pillarNodeId, mapId, error: msg };
  }
  const outlinePrompt = compilePromptSafe(
    outlinePromptDb.template,
    pillarVars,
    "outline_generation_pillar",
    "Generate pillar outline",
  );
  logVerbose("PillarGen", "Outline prompt (FULL)", { prompt: outlinePrompt });

  let outline: PillarOutline = PILLAR_FALLBACK_OUTLINE;
  let outlineRawText = "";
  try {
    const outlineStageCfg = getStageConfig("outline");
    const provider = getLLMProvider(outlinePromptDb.provider || outlineStageCfg.provider);
    const model = outlinePromptDb.modelId || outlineStageCfg.model;
    const llmStart = Date.now();
    const response = await provider.call(outlinePrompt, {
      model,
      step: "outline_generation_pillar",
      temperature: outlinePromptDb.temperature ?? undefined,
    });
    outlineRawText = response.text || "";
    totalCostUsd += response.costUsd ?? 0;
    logLlm("outline_generation_pillar", {
      model,
      provider: outlinePromptDb.provider || outlineStageCfg.provider,
      temperature: outlinePromptDb.temperature,
    }, {
      tokensInput: (response as { tokensInput?: number }).tokensInput,
      tokensOutput: (response as { tokensOutput?: number }).tokensOutput,
      costUsd: response.costUsd ?? 0,
      durationMs: Date.now() - llmStart,
      responseLength: outlineRawText.length,
    });
    logVerbose("PillarGen", "Outline raw response (FULL)", { response_text: outlineRawText });
    outline = parsePillarOutline(outlineRawText);
    logPublic("PillarGen", `Outline OK: ${outline.h2_sections.length} H2 sections`);
  } catch (err) {
    const msg = `Outline LLM call failed: ${(err as Error).message}`;
    logError("PillarGen", msg);
    // Continue with fallback outline (1 H2 "Przegląd silosa")
  }

  // ---------------------------------------------
  // Stage 4: Intro (intro_generation_pillar)
  // ---------------------------------------------
  onProgress?.("Generating intro...", "intro");
  const introPromptDb = await loadPrompt("intro_generation_pillar" as never);
  let introHtml = "<p>Wstęp do silosa tematycznego.</p>";
  if (introPromptDb) {
    const introPrompt = compilePromptSafe(
      introPromptDb.template,
      pillarVars,
      "intro_generation_pillar",
      "Generate pillar intro",
    );
    logVerbose("PillarGen", "Intro prompt (FULL)", { prompt: introPrompt });
    try {
      const introStageCfg = getStageConfig("draft");
      const provider = getLLMProvider(introPromptDb.provider || introStageCfg.provider);
      const model = introPromptDb.modelId || introStageCfg.model;
      const llmStart = Date.now();
      const response = await provider.call(introPrompt, {
        model,
        step: "intro_generation_pillar",
        temperature: introPromptDb.temperature ?? undefined,
      });
      introHtml = response.text || introHtml;
      totalCostUsd += response.costUsd ?? 0;
      logLlm("intro_generation_pillar", {
        model,
        provider: introPromptDb.provider || introStageCfg.provider,
        temperature: introPromptDb.temperature,
      }, {
        tokensInput: (response as { tokensInput?: number }).tokensInput,
        tokensOutput: (response as { tokensOutput?: number }).tokensOutput,
        costUsd: response.costUsd ?? 0,
        durationMs: Date.now() - llmStart,
        responseLength: introHtml.length,
      });
      logVerbose("PillarGen", "Intro raw response (FULL)", { response_text: introHtml });
    } catch (err) {
      logError("PillarGen", `Intro LLM call failed: ${(err as Error).message}`);
    }
  } else {
    logError("PillarGen", "Prompt 'intro_generation_pillar' not found, using fallback");
  }

  // ---------------------------------------------
  // Stage 5: Sections per H2 (section_generation_pillar)
  // ---------------------------------------------
  const sectionPromptDb = await loadPrompt("section_generation_pillar" as never);
  if (!sectionPromptDb) {
    const msg = "Prompt 'section_generation_pillar' not found in DB";
    logError("PillarGen", msg);
    endLogRun({ success: false, error: msg });
    return { success: false, costUsd: totalCostUsd, pillarNodeId, mapId, error: msg };
  }

  // P-A (faza P): pre-compute Map<normalized_title, PillarSpokeMember> dla per-section matching.
  // Outline zwraca section.internal_links jako TYTUŁY spoków (Cowork v2 prompt: "dokładny tytuł
  // artykułu z listy"). Match po normalized title (trim + lowercase) z ctx.cluster_member_spokes.
  // Tylko published spoki (is_published && wp_post_url) wchodza do per-section listy.
  const normalizeTitle = (s: string): string => s.trim().toLowerCase();
  const publishedSpokesByTitle = new Map<string, PillarSpokeMember>();
  for (const s of ctx.cluster_member_spokes) {
    if (s.is_published && s.wp_post_url) {
      publishedSpokesByTitle.set(normalizeTitle(s.title), s);
    }
  }
  const perSectionLinkCounts: Array<{ h2: string; assigned: number; total_published: number }> = [];
  // P-G: rejestr retries dla forensics (engine_debug.pillar.per_section_retries)
  const perSectionRetries: Array<{
    h2: string;
    missing_before: string[];
    missing_after: string[];
    recovered: number;
    retry_cost_usd: number;
    outcome: "retry_recovered_all" | "retry_partial" | "retry_no_improvement";
  }> = [];

  const sectionsHtml: string[] = [];
  for (let i = 0; i < outline.h2_sections.length; i++) {
    const section = outline.h2_sections[i];
    onProgress?.(`Generating H2 (${i + 1}/${outline.h2_sections.length}): ${section.title}`, "sections");

    const h3sFormatted = (section.h3_subpoints || []).length > 0
      ? (section.h3_subpoints || []).map((h3) => `- ${h3}`).join("\n")
      : "(brak H3 dla tej sekcji)";

    // P-A: mapuj section.internal_links (tytuły z outline) -> spoki published, format /slug — title.
    // Sekcja z 0 przypisań -> placeholder ucinajacy LLM od wymyslania linkow.
    const assignedTitles = Array.isArray(section.internal_links) ? section.internal_links : [];
    const assignedSpokeLines: string[] = [];
    const matchedSlugs: string[] = [];
    // P-G: Map slug -> title dla mandatory inject block w retry (gdy LLM pomija assigned link)
    const assignedSlugToTitle = new Map<string, string>();
    const unmatchedTitles: string[] = [];
    for (const rawTitle of assignedTitles) {
      const norm = normalizeTitle(rawTitle);
      const spoke = publishedSpokesByTitle.get(norm);
      if (!spoke || !spoke.wp_post_url) {
        unmatchedTitles.push(rawTitle);
        continue;
      }
      const slugPath = extractSlugFromUrl(spoke.wp_post_url);
      if (!slugPath) {
        unmatchedTitles.push(rawTitle);
        continue;
      }
      assignedSpokeLines.push(`- ${slugPath} — ${spoke.title} (hub_to_spoke)`);
      matchedSlugs.push(slugPath);
      assignedSlugToTitle.set(slugPath, spoke.title);
    }
    const perSectionSlugs = assignedSpokeLines.length > 0
      ? assignedSpokeLines.join("\n")
      : "(ta sekcja nie ma przypisanych artykułów — nie wstawiaj linków)";
    perSectionLinkCounts.push({
      h2: section.title,
      assigned: assignedSpokeLines.length,
      total_published: publishedSpokesByTitle.size,
    });
    logPublic(
      "PillarGen",
      `Section ${i + 1} "${section.title.slice(0, 50)}": ${assignedSpokeLines.length} linkow przypisanych (z ${assignedTitles.length} w outline, ${unmatchedTitles.length} unmatched)`,
    );
    if (unmatchedTitles.length > 0) {
      logDebug("PillarGen", `Section ${i + 1} unmatched outline titles`, { unmatched: unmatchedTitles, available_titles: [...publishedSpokesByTitle.keys()].slice(0, 5) });
    }

    const sectionVars = {
      ...pillarVars,
      current_h2_title: section.title,
      h3s_formatted: h3sFormatted,
      // P-A: nadpisz globalny cluster_member_slugs (wszystkie spoki) per-section subsetem.
      // LLM widzi TYLKO linki przypisane do tej sekcji -> brak over-linkingu (jeden /slug 4x),
      // brak sierot (bo distribution outline zapewnia 1:1).
      cluster_member_slugs: perSectionSlugs,
    };

    const sectionPrompt = compilePromptSafe(
      sectionPromptDb.template,
      sectionVars,
      "section_generation_pillar",
      `<h2>${section.title}</h2><p>(blad generacji)</p>`,
    );
    logVerbose("PillarGen", `Section ${i + 1} prompt (FULL)`, { h2: section.title, prompt: sectionPrompt });

    try {
      const sectionStageCfg = getStageConfig("draft");
      const provider = getLLMProvider(sectionPromptDb.provider || sectionStageCfg.provider);
      const model = sectionPromptDb.modelId || sectionStageCfg.model;
      const llmStart = Date.now();
      const response = await provider.call(sectionPrompt, {
        model,
        step: "section_generation_pillar",
        temperature: sectionPromptDb.temperature ?? undefined,
      });
      let sectionHtml = response.text || `<h2>${section.title}</h2><p>(brak treści)</p>`;
      totalCostUsd += response.costUsd ?? 0;
      const tokensInSec = (response as { tokensInput?: number }).tokensInput ?? 0;
      const tokensOutSec = (response as { tokensOutput?: number }).tokensOutput ?? 0;
      logLlm("section_generation_pillar", {
        model,
        provider: sectionPromptDb.provider || sectionStageCfg.provider,
        temperature: sectionPromptDb.temperature,
      }, {
        tokensInput: tokensInSec,
        tokensOutput: tokensOutSec,
        costUsd: response.costUsd ?? 0,
        durationMs: Date.now() - llmStart,
        responseLength: sectionHtml.length,
      });
      // P-D: metryki danych w logPublic per sekcja (widoczne w panelu LOGI prod)
      logPublic(
        "PillarGen",
        `Section ${i + 1} OK: ${sectionHtml.length} chars | tokens in=${tokensInSec}/out=${tokensOutSec} | cost=$${(response.costUsd ?? 0).toFixed(4)} | ${Math.round((Date.now() - llmStart) / 1000)}s`,
      );

      // P-G (faza P hotfix): omission guard — czy LLM wstawil wszystkie assigned slugi?
      // Jesli brakuje -> retry RAZ z mandatory inject block (bez P-F literowki, bo
      // canonicalize dziala POST-section w post-check; literowka w retry tez zostanie poprawiona).
      if (matchedSlugs.length > 0) {
        const missingBefore = findMissingAssignedLinks(sectionHtml, matchedSlugs);
        if (missingBefore.length > 0) {
          logPublic(
            "PillarGen",
            `Section ${i + 1} OMISSION: brakuje ${missingBefore.length}/${matchedSlugs.length} assigned linkow (${missingBefore.join(", ")}). Retry RAZ z mandatory inject block.`,
          );
          const missingTitlesBlock = missingBefore
            .map((slug) => `  - ${slug} — ${assignedSlugToTitle.get(slug) || "(unknown title)"}`)
            .join("\n");
          const mandatoryBlock = `

OBOWIĄZKOWE LINKI (retry — poprzedni draft pominął te artykuły, NIE wolno ich pominąć ponownie):
${missingTitlesBlock}

Wpleć NATURALNIE w zdanie, pokazując RELACJĘ między tematem tej sekcji a docelowym artykułem (np. "X zależy od Y, które rozwija <a href=\\"/y\\">opisowy anchor</a>"). Anchor opisowy, bogaty w pojęcia z tematu — NIGDY "zobacz też", "kliknij", "tutaj". KAŻDY z tych /slug MUSI się pojawić w tej sekcji jako klikalny <a href> dokładnie jak podano (bez modyfikacji litery, case, slash). Bez stopki, bez sztucznego akapitu "powiązane artykuły".`;

          const retryPrompt = sectionPrompt + mandatoryBlock;
          logVerbose("PillarGen", `Section ${i + 1} retry prompt (with mandatory inject)`, { h2: section.title, prompt: retryPrompt });

          let retryOutcome: "retry_recovered_all" | "retry_partial" | "retry_no_improvement" = "retry_no_improvement";
          let missingAfter: string[] = missingBefore;
          let retryCostUsd = 0;
          try {
            const retryStart = Date.now();
            const retryResponse = await provider.call(retryPrompt, {
              model,
              step: "section_generation_pillar",
              temperature: sectionPromptDb.temperature ?? undefined,
            });
            const retryHtml = retryResponse.text || sectionHtml;
            const retryMissing = findMissingAssignedLinks(retryHtml, matchedSlugs);
            retryCostUsd = retryResponse.costUsd ?? 0;
            totalCostUsd += retryCostUsd;
            const retryTokensIn = (retryResponse as { tokensInput?: number }).tokensInput ?? 0;
            const retryTokensOut = (retryResponse as { tokensOutput?: number }).tokensOutput ?? 0;
            logLlm("section_generation_pillar_retry", {
              model,
              provider: sectionPromptDb.provider || sectionStageCfg.provider,
              temperature: sectionPromptDb.temperature,
            }, {
              tokensInput: retryTokensIn,
              tokensOutput: retryTokensOut,
              costUsd: retryCostUsd,
              durationMs: Date.now() - retryStart,
              responseLength: retryHtml.length,
            });

            if (retryMissing.length < missingBefore.length) {
              // Retry pokrył więcej (recovered ≥ 1) — użyj retried HTML
              sectionHtml = retryHtml;
              missingAfter = retryMissing;
              retryOutcome = retryMissing.length === 0 ? "retry_recovered_all" : "retry_partial";
              logPublic(
                "PillarGen",
                `Section ${i + 1} retry ${retryOutcome === "retry_recovered_all" ? "OK" : "partial"}: ${missingBefore.length - retryMissing.length}/${missingBefore.length} omission(s) recovered | retry cost=$${retryCostUsd.toFixed(4)} | tokens in=${retryTokensIn}/out=${retryTokensOut}`,
              );
              if (retryMissing.length > 0) {
                logError(
                  "PillarGen",
                  `WARNING: Section ${i + 1} still missing ${retryMissing.length} assigned slug(s) after retry: ${retryMissing.join(", ")} (relacja hub_to_spoke pokrywa graf, ale link w prozie nieobecny)`,
                );
              }
            } else {
              // Retry nie pomógł — zostaw oryginalny (logError WARNING)
              missingAfter = missingBefore;
              retryOutcome = "retry_no_improvement";
              logError(
                "PillarGen",
                `WARNING: Section ${i + 1} retry no-improvement (still missing: ${retryMissing.join(", ")}), keeping original | retry cost=$${retryCostUsd.toFixed(4)}`,
              );
            }
          } catch (retryErr) {
            logError("PillarGen", `Section ${i + 1} retry LLM call failed: ${(retryErr as Error).message}`);
            missingAfter = missingBefore;
            retryOutcome = "retry_no_improvement";
          }

          perSectionRetries.push({
            h2: section.title,
            missing_before: missingBefore,
            missing_after: missingAfter,
            recovered: missingBefore.length - missingAfter.length,
            retry_cost_usd: retryCostUsd,
            outcome: retryOutcome,
          });
        }
      }

      sectionsHtml.push(sectionHtml);
    } catch (err) {
      logError("PillarGen", `Section ${i + 1} LLM call failed: ${(err as Error).message}`);
      sectionsHtml.push(`<h2>${section.title}</h2><p>(blad generacji sekcji)</p>`);
    }
  }

  // ---------------------------------------------
  // Stage 6: Concatenate intro + sections
  // ---------------------------------------------
  const rawHtml = `<h1>${pillarTitle}</h1>\n${introHtml}\n\n${sectionsHtml.join("\n\n")}`;
  logPublic("PillarGen", `Draft assembled: ${rawHtml.length} chars (${sectionsHtml.length} sections)`);

  // ---------------------------------------------
  // Stage 7: Optimization (share)
  // ---------------------------------------------
  onProgress?.("Optimizing...", "optimization");
  let optimizedHtml = rawHtml;
  try {
    const optResult = await optimizeArticle(rawHtml, "pl", 3000);
    optimizedHtml = optResult.optimizedHtml || rawHtml;
    totalCostUsd += optResult.costUsd ?? 0;
    // P-D: metryka w logPublic (delta chars + cost)
    logPublic(
      "PillarGen",
      `Optimization OK: ${rawHtml.length} -> ${optimizedHtml.length} chars (${optimizedHtml.length - rawHtml.length >= 0 ? "+" : ""}${optimizedHtml.length - rawHtml.length}) | cost=$${(optResult.costUsd ?? 0).toFixed(4)}`,
    );
  } catch (err) {
    logError("PillarGen", `Optimization failed (non-blocking): ${(err as Error).message}`);
  }

  // ---------------------------------------------
  // Stage 8: Humanization (share)
  // ---------------------------------------------
  onProgress?.("Humanizing...", "humanization");
  let humanizedHtml = optimizedHtml;
  try {
    const humResult = await humanizeArticle(optimizedHtml, mapId, pillarNodeId, "pl");
    humanizedHtml = humResult.humanizedHtml || optimizedHtml;
    totalCostUsd += humResult.costUsd ?? 0;
    // P-D: metryka w logPublic (delta chars + cost)
    logPublic(
      "PillarGen",
      `Humanization OK: ${optimizedHtml.length} -> ${humanizedHtml.length} chars (${humanizedHtml.length - optimizedHtml.length >= 0 ? "+" : ""}${humanizedHtml.length - optimizedHtml.length}) | cost=$${(humResult.costUsd ?? 0).toFixed(4)}`,
    );
  } catch (err) {
    logError("PillarGen", `Humanization failed (non-blocking): ${(err as Error).message}`);
  }

  // ---------------------------------------------
  // Stage 9: SKIPPED (insertSources for claims) — P5 osobno
  // ---------------------------------------------

  // ---------------------------------------------
  // Stage 10: postCheckPillarLinks (dedupe + footer + refresh relations)
  // ---------------------------------------------
  onProgress?.("Post-check links (dedupe + footer + relations)...", "post_check");
  const postCheckSpokes = mapSpokesForPostCheck(ctx.cluster_member_spokes);
  let postCheck: PillarLinkPostCheckResult | null = null;
  let finalHtml = humanizedHtml;
  try {
    postCheck = await postCheckPillarLinks(
      humanizedHtml,
      pillarNodeId,
      mapId,
      pillarUserId,
      postCheckSpokes,
    );
    finalHtml = postCheck.html;
    logPublic(
      "PillarGen",
      `Post-check OK: dedupe=${postCheck.deduplicated_count} footer_orphans=${postCheck.orphans.length} relations=+${postCheck.node_relations_refreshed}`,
    );
  } catch (err) {
    logError("PillarGen", `Post-check failed (non-blocking): ${(err as Error).message}`);
  }

  // ---------------------------------------------
  // Stage 12: Persist do thematic_node_content
  // ---------------------------------------------
  onProgress?.("Persisting...", "persist");
  const nowIso = new Date().toISOString();
  const debugPayload: PillarGenerationDebug = {
    outline_prompt_key: "outline_generation_pillar",
    intro_prompt_key: "intro_generation_pillar",
    section_prompt_key: "section_generation_pillar",
    outline_chars: outlineRawText.length,
    intro_chars: introHtml.length,
    sections_count: sectionsHtml.length,
    raw_html_chars: rawHtml.length,
    optimized_html_chars: optimizedHtml.length,
    final_html_chars: finalHtml.length,
    cluster_member_spokes_count: ctx.cluster_member_spokes.length,
    published_spokes_count: publishedSpokes.length,
    cluster_member_entities_count: ctx.cluster_member_entities.length,
    pre_step_relations_created: preStepCreated,
    pre_step_relations_deleted: preStepDeleted,
    post_check: postCheck,
    per_section_link_counts: perSectionLinkCounts,
    per_section_retries: perSectionRetries,
    total_cost_usd: totalCostUsd,
    total_duration_ms: Date.now() - startTime,
  };

  try {
    const { error: upsertErr } = await (supabase as any)
      .from("thematic_node_content")
      .upsert(
        {
          user_id: pillarUserId,
          map_id: mapId,
          node_id: pillarNodeId,
          outline: outline as unknown as Record<string, unknown>,
          draft_html: rawHtml,
          optimized_html: optimizedHtml,
          final_html: finalHtml,
          // BEZ tego pillar zostawał na default 'empty' → licznik "Treść" w pasku
          // postępu mapy (ThematicMapDetailPage) pomijał pillary (np. 180/200
          // zamiast 200/200). Backfill istniejących: 2026-08-05 (29 wierszy).
          content_status: "content_ready",
          engine_stage: "ready",
          engine_started_at: new Date(startTime).toISOString(),
          engine_completed_at: nowIso,
          engine_cost: totalCostUsd,
          engine_debug: { pillar: debugPayload },
          updated_at: nowIso,
        },
        { onConflict: "node_id" },
      );

    if (upsertErr) {
      const msg = `UPSERT thematic_node_content failed: ${upsertErr.message}`;
      logError("PillarGen", msg);
      endLogRun({ success: false, error: msg });
      return { success: false, costUsd: totalCostUsd, pillarNodeId, mapId, error: msg, debug: debugPayload };
    }
  } catch (err) {
    const msg = `Persist exception: ${(err as Error).message}`;
    logError("PillarGen", msg);
    endLogRun({ success: false, error: msg });
    return { success: false, costUsd: totalCostUsd, pillarNodeId, mapId, error: msg, debug: debugPayload };
  }

  logPublic(
    "PillarGen",
    `END — final=${finalHtml.length} chars, cost=$${totalCostUsd.toFixed(4)}, ${(debugPayload.total_duration_ms / 1000).toFixed(1)}s`,
  );
  endLogRun({
    success: true,
    final_html_chars: finalHtml.length,
    total_cost_usd: totalCostUsd,
    sections_count: sectionsHtml.length,
    pre_step_created: preStepCreated,
    post_check_dedupe: postCheck?.deduplicated_count ?? 0,
    post_check_orphans: postCheck?.orphans.length ?? 0,
    duration_s: Math.round(debugPayload.total_duration_ms / 1000),
  });

  return {
    success: true,
    html: finalHtml,
    costUsd: totalCostUsd,
    pillarNodeId,
    mapId,
    debug: debugPayload,
  };
}

// =============================================
// "Refresh links" idempotent — bez regeneracji LLM
// =============================================

/**
 * Reuse-able dla button "Odśwież linki" w UI (STOP-P3).
 * Load existing final_html z thematic_node_content -> create/refresh relations + post-check (dedupe + footer)
 * -> UPDATE final_html.
 *
 * Idempotent: re-run identyczny output gdy DB state unchanged.
 */
export async function refreshPillarLinks(
  pillarNodeId: string,
  mapId: string,
): Promise<{ success: boolean; post_check: PillarLinkPostCheckResult | null; error?: string }> {
  const runId = `pillar-refresh-${pillarNodeId.slice(0, 8)}-${Date.now()}`;
  startLogRun(runId, { type: "pillar_refresh_links", pillarNodeId, mapId });
  logPublic("PillarRefresh", `START pillar=${pillarNodeId.slice(0, 8)}`);

  // 1. Load existing content
  const { data: contentRow, error: loadErr } = await (supabase as any)
    .from("thematic_node_content")
    .select("user_id, final_html, engine_stage")
    .eq("node_id", pillarNodeId)
    .single();

  if (loadErr || !contentRow) {
    const msg = `Pillar content not found: ${loadErr?.message || "no row"}`;
    logError("PillarRefresh", msg);
    endLogRun({ success: false, error: msg });
    return { success: false, post_check: null, error: msg };
  }
  if (!contentRow.final_html) {
    const msg = "Pillar final_html is empty — generate pillar first";
    logError("PillarRefresh", msg);
    endLogRun({ success: false, error: msg });
    return { success: false, post_check: null, error: msg };
  }

  const pillarUserId = contentRow.user_id as string;
  const currentHtml = contentRow.final_html as string;

  // 2. Load cluster context (current DB state — uwzglednia nowo opublikowane spoki)
  const ctx = await getPillarClusterContext(mapId, pillarNodeId);
  if (!ctx) {
    const msg = "Pillar cluster context not loaded";
    logError("PillarRefresh", msg);
    endLogRun({ success: false, error: msg });
    return { success: false, post_check: null, error: msg };
  }

  const publishedSpokes = ctx.cluster_member_spokes.filter((s) => s.is_published && s.wp_post_url);

  // 3. Refresh hub_to_spoke relations (DELETE + INSERT z aktualnego DB state)
  try {
    await createHubToSpokeRelations(pillarNodeId, mapId, pillarUserId, publishedSpokes);
  } catch (err) {
    logError("PillarRefresh", `Relations refresh failed: ${(err as Error).message}`);
  }

  // 4. Post-check (dedupe + footer)
  const postCheckSpokes = mapSpokesForPostCheck(ctx.cluster_member_spokes);
  let postCheck: PillarLinkPostCheckResult | null = null;
  try {
    postCheck = await postCheckPillarLinks(
      currentHtml,
      pillarNodeId,
      mapId,
      pillarUserId,
      postCheckSpokes,
    );
  } catch (err) {
    const msg = `Post-check failed: ${(err as Error).message}`;
    logError("PillarRefresh", msg);
    endLogRun({ success: false, error: msg });
    return { success: false, post_check: null, error: msg };
  }

  // 5. UPDATE final_html jesli zmieniony
  if (postCheck.html !== currentHtml) {
    const { error: updateErr } = await (supabase as any)
      .from("thematic_node_content")
      .update({
        final_html: postCheck.html,
        updated_at: new Date().toISOString(),
      })
      .eq("node_id", pillarNodeId);

    if (updateErr) {
      logError("PillarRefresh", `UPDATE final_html failed: ${updateErr.message}`);
    } else {
      logPublic("PillarRefresh", `HTML updated: dedupe=${postCheck.deduplicated_count} orphans=${postCheck.orphans.length}`);
    }
  } else {
    logPublic("PillarRefresh", "HTML unchanged (already optimal)");
  }

  endLogRun({
    success: true,
    dedupe: postCheck.deduplicated_count,
    orphans_in_footer: postCheck.orphans.length,
    relations_refreshed: postCheck.node_relations_refreshed,
  });

  return { success: true, post_check: postCheck };
}

// =============================================
// Helper: VITE_PILLAR_FLOW_V2_ENABLED check (env-time, default TRUE od 2026-06-02).
//
// Historia: do 2026-06-01 default było false (opt-in per deploy). Od 2026-06-01
// thematic_maps.pillar_pages_enabled DEFAULT true → globalna ścieżka env też
// powinna być default true dla spójności. Kill-switch zachowany jako explicit
// VITE_PILLAR_FLOW_V2_ENABLED=false (np. emergency wyłącz V2 w prod bez deploy
// nowego kodu). Per-mapa granularność dalej w DB column pillar_pages_enabled.
// =============================================
export function isPillarFlowV2Enabled(): boolean {
  const fromVite =
    typeof import.meta !== "undefined" &&
    (import.meta as { env?: { VITE_PILLAR_FLOW_V2_ENABLED?: string } }).env?.VITE_PILLAR_FLOW_V2_ENABLED;
  const fromProcess =
    typeof process !== "undefined" ? process.env.VITE_PILLAR_FLOW_V2_ENABLED : undefined;
  const value = fromVite || fromProcess;
  // Default true — tylko explicit "false" wyłącza (kill-switch semantics)
  return value !== "false";
}
